import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import config from '../config';
import { useRules } from '../context/RuleContext';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

export default function PushToServer({ user }) {
  const { exportToXml, fetchFromDb } = useRules();
  const [host] = useState('partnerupload.google.com');
  const [port] = useState('19321');
  const [sftpUsername] = useState('feeds-3d7i05');
  const [sftpPassword] = useState('configured');
  const [remotePath] = useState('/FinalAllTravomint.xml');
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [otpStep, setOtpStep] = useState(false); // true = waiting for OTP input
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [gitPushing, setGitPushing] = useState(false);
  const [gitResult, setGitResult] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionDetail, setVersionDetail] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewXml, setPreviewXml] = useState('');
  const [syncingMarkup, setSyncingMarkup] = useState(false);
  const [markupSyncResult, setMarkupSyncResult] = useState(null);

  // Fetch last 5 versions
  useEffect(() => {
    fetch(`${config.API_BASE}/rulefile/versions`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setVersions(data); })
      .catch(() => {});
  }, [uploadResult]); // refresh after upload

  const handleViewVersion = async (versionId) => {
    try {
      const res = await fetch(`${config.API_BASE}/rulefile/versions/${versionId}`);
      const data = await res.json();
      if (data.success) {
        // Parse the XML to get a summary
        const content = data.content || '';
        const feeRuleCount = (content.match(/<FeeRule /g) || []).length;
        const locationGroupCount = (content.match(/<LocationGroup>/g) || []).length;
        const carrierGroupCount = (content.match(/<CarrierGroup>/g) || []).length;
        const suppressCount = (content.match(/<Suppress>true<\/Suppress>/g) || []).length;
        const discountCount = (content.match(/FeePercent>-/g) || []).length + (content.match(/FeeAmount>[A-Z]{3}-/g) || []).length;
        const hasTestRuleSet = content.includes('<TestRuleSet>');
        const testRuleCount = hasTestRuleSet ? (content.split('<TestRuleSet>')[1]?.match(/<FeeRule /g) || []).length : 0;

        setVersionDetail({
          ...data,
          summary: { feeRuleCount, locationGroupCount, carrierGroupCount, suppressCount, discountCount, testRuleCount, hasTestRuleSet }
        });
        setDetailDialogOpen(true);
      }
    } catch { toast.error('Failed to load version'); }
  };

  const handleRestore = async (versionId) => {
    if (!window.confirm('Restore this version as the current DB file?')) return;
    try {
      const res = await fetch(`${config.API_BASE}/rulefile/restore/${versionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, role: user?.role }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        fetchFromDb(); // reload the restored file
      } else { toast.error(data.message); }
    } catch { toast.error('Backend error'); }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setTestResult(null);
    try {
      const res = await fetch(`${config.API_BASE}/sftp/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host, port, sftpUsername, sftpPassword }),
      });
      const data = await res.json();
      setTestResult(data);
      if (data.success) toast.success('Connection OK!');
      else toast.error(data.message);
    } catch { toast.error('Backend not available'); }
    finally { setLoading(false); }
  };

  const handleUploadClick = () => {
    setConfirmOpen(true);
    setOtpStep(false);
    setOtp('');
    setOtpError('');
    setOtpSent(false);
    setChangeSummary('');
    setGitResult(null);
  };

  const handleGitPushFirst = async () => {
    if (!changeSummary.trim()) { setOtpError('Please enter a summary of changes'); return; }
    setGitPushing(true);
    setGitResult(null);
    try {
      const res = await fetch(`${config.API_BASE}/sftp/git-push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, role: user?.role, summary: changeSummary.trim() }),
      });
      const data = await res.json();
      setGitResult(data);
      if (data.success) {
        toast.success('Pushed to Git successfully');
        // Now move to OTP step for Google upload
        setOtpStep(true);
      } else {
        toast.error(data.message || 'Git push failed');
      }
    } catch { toast.error('Backend not available'); }
    finally { setGitPushing(false); }
  };

  const handleSendOtp = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      const res = await fetch(`${config.API_BASE}/sftp/generate-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, role: user?.role }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpStep(true);
        setOtpSent(true);
        toast.success('OTP sent to developer@travomint.com');
      } else {
        setOtpError(data.message);
        toast.error(data.message);
      }
    } catch { setOtpError('Failed to send OTP'); }
    finally { setOtpSending(false); }
  };

  const handleVerifyAndUpload = async () => {
    if (!otp.trim()) { setOtpError('Enter the OTP'); return; }
    setLoading(true);
    setOtpError('');
    try {
      // Verify OTP first
      const otpRes = await fetch(`${config.API_BASE}/sftp/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, otp: otp.trim() }),
      });
      const otpData = await otpRes.json();
      if (!otpData.success) {
        setOtpError(otpData.message);
        setLoading(false);
        return;
      }
      // OTP valid — proceed with upload
      setConfirmOpen(false);
      setUploadResult(null);
      const res = await fetch(`${config.API_BASE}/sftp/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, role: user?.role }),
      });
      const data = await res.json();
      setUploadResult(data);
      if (data.success) {
        toast.success('Uploaded to Google SFTP!');
        handleDownloadBackup();
      } else {
        toast.error(data.message);
      }
    } catch { toast.error('Backend not available'); }
    finally { setLoading(false); }
  };

  const handleConfirmUpload = async () => {
    // This is now replaced by the OTP flow — just call handleSendOtp
    handleSendOtp();
  };

  const handleDownloadBackup = () => {
    const xml = exportToXml();
    const blob = new Blob([xml], { type: 'application/xml' });
    const timestamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-');
    saveAs(blob, `FinalAllTravomint-published-${timestamp}.xml`);
  };

  const handlePreviewFile = () => {
    const xml = exportToXml();
    setPreviewXml(xml);
    setPreviewOpen(true);
  };

  const handleSyncMarkup = async () => {
    setSyncingMarkup(true);
    setMarkupSyncResult(null);
    try {
      const res = await fetch(`${config.API_BASE}/sftp/sync-markup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, role: user?.role }),
      });
      const data = await res.json();
      setMarkupSyncResult(data);
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
    } catch { toast.error('Backend not available'); }
    finally { setSyncingMarkup(false); }
  };

  if (user?.role !== 'ADMIN') {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Push to Google Server</Typography>
        <Alert severity="warning">Only ADMIN can upload files to the Google SFTP server.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Push to Google SFTP Server</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Upload the current database rule file to Google's SFTP server. The file goes live on Google Flights within about an hour.
        <br /><strong>A backup copy will be downloaded automatically after upload.</strong>
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>SFTP Server</Typography>
          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: 13 }}>
            sftp://{sftpUsername}@{host}:{port}{remotePath}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Credentials are configured server-side. No password entry required.
          </Typography>
        </CardContent>
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="outlined" color="info" startIcon={<VisibilityIcon />}
          onClick={handlePreviewFile}>
          View File Before Upload
        </Button>
        <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <WifiTetheringIcon />}
          onClick={handleTestConnection} disabled={loading}>
          Test Connection
        </Button>
        <Button variant="contained" color="success" size="large"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          onClick={handleUploadClick} disabled={loading}>
          Upload to Google
        </Button>
        {/* <Button variant="contained" color="secondary"
          startIcon={syncingMarkup ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          onClick={handleSyncMarkup} disabled={syncingMarkup}>
          {syncingMarkup ? 'Syncing...' : 'Sync to Markup DB'}
        </Button> */}
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadBackup}>
          Download File Copy
        </Button>
      </Box>

      {/* Test Result */}
      {testResult && (
        <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
          {testResult.message}
        </Alert>
      )}

      {/* Upload Result */}
      {uploadResult && (
        <Card sx={{ borderLeft: `4px solid ${uploadResult.success ? '#4caf50' : '#f44336'}`, mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {uploadResult.success ? <CheckCircleIcon color="success" /> : <WarningAmberIcon color="error" />}
              <Typography variant="h6" color={uploadResult.success ? 'success.main' : 'error.main'}>
                {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>{uploadResult.message}</Typography>
            {uploadResult.bytes && <Chip label={`${uploadResult.bytes} bytes sent`} size="small" sx={{ mt: 1 }} />}
            {uploadResult.success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                ✓ File published to Google. A backup copy has been downloaded to your computer.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Markup Sync Result - disabled for now */}

      {/* Confirmation + OTP Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" /> {otpStep ? 'Enter OTP to Upload to Google' : 'Push to Git & Upload to Google'}
        </DialogTitle>
        <DialogContent>
          {!otpStep ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Step 1:</strong> Push the rule file to Git (Bitbucket). Then you'll verify OTP to upload to Google.
              </Alert>
              <TextField
                fullWidth label="Summary of Changes" value={changeSummary}
                onChange={(e) => { setChangeSummary(e.target.value); setOtpError(''); }}
                placeholder="e.g. Added 5% discount for AI on US routes, Extended dates for carrier group 9"
                multiline rows={3}
                error={!!otpError}
                helperText={otpError || 'Describe what changed — this goes in the git commit message'}
                sx={{ mb: 2 }}
                autoFocus
              />
              {gitResult && (
                <Alert severity={gitResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                  {gitResult.message}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                ✓ Git push complete! Now verify OTP to upload to Google SFTP.
              </Alert>
              <Alert severity="info" sx={{ mb: 2 }}>
                An OTP will be sent to <strong>developer@travomint.com</strong> for verification.
              </Alert>
              {!otpSent && !otpSending && (
                <Button variant="outlined" color="warning" onClick={handleConfirmUpload} disabled={otpSending}
                  startIcon={otpSending ? <CircularProgress size={14} /> : null} sx={{ mb: 2 }}>
                  Send OTP
                </Button>
              )}
              {(otpSent || otpSending) && (
                <TextField
                  fullWidth
                  label="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value); setOtpError(''); }}
                  placeholder="000000"
                  inputProps={{ maxLength: 6, style: { fontSize: '1.5rem', letterSpacing: 8, textAlign: 'center' } }}
                  error={!!otpError}
                  helperText={otpError || 'Check developer@travomint.com inbox. Valid for 5 minutes.'}
                  sx={{ mt: 1 }}
                  autoFocus
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          {!otpStep ? (
            <Button variant="contained" color="primary"
              startIcon={gitPushing ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              onClick={handleGitPushFirst} disabled={gitPushing || !changeSummary.trim()}>
              {gitPushing ? 'Pushing to Git...' : 'Push to Git'}
            </Button>
          ) : (
            otpSent && (
              <Button variant="contained" color="success"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                onClick={handleVerifyAndUpload} disabled={loading || otp.length < 6}>
                {loading ? 'Uploading...' : 'Verify & Upload to Google'}
              </Button>
            )
          )}
        </DialogActions>
      </Dialog>

      {/* Version History */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            <RestoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Last 5 Saved Versions
          </Typography>
          {versions.length === 0 ? (
            <Typography color="text.secondary">No versions saved yet. Save to DB to create the first version.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Saved At</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Saved By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rules</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versions.map((v, idx) => (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        <Typography variant="caption">{new Date(v.savedAt).toLocaleString()}</Typography>
                        {idx === 0 && <Chip label="Latest" size="small" color="success" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell><Chip label={v.savedBy} size="small" variant="outlined" /></TableCell>
                      <TableCell>{v.ruleCount}</TableCell>
                      <TableCell><Typography variant="caption">{Math.round(v.contentLength / 1024)} KB</Typography></TableCell>
                      <TableCell><Typography variant="caption">{v.description}</Typography></TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => handleViewVersion(v.id)} sx={{ mr: 0.5 }}>
                          Overview
                        </Button>
                        {idx > 0 && (
                          <Button size="small" color="warning" startIcon={<RestoreIcon />}
                            onClick={() => handleRestore(v.id)}>
                            Restore
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {/* Version Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Version Overview</DialogTitle>
        <DialogContent>
          {versionDetail && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Saved By</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{versionDetail.savedBy}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Saved At</Typography>
                  <Typography variant="body1">{new Date(versionDetail.savedAt).toLocaleString()}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>File Summary</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="h5" color="primary">{versionDetail.summary.feeRuleCount}</Typography>
                      <Typography variant="caption">Fee Rules</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="h5" color="success.main">{versionDetail.summary.locationGroupCount}</Typography>
                      <Typography variant="caption">Location Groups</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="h5" color="warning.main">{versionDetail.summary.carrierGroupCount}</Typography>
                      <Typography variant="caption">Carrier Groups</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="h5" color="error.main">{versionDetail.summary.suppressCount}</Typography>
                      <Typography variant="caption">Suppression</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="h5" color="secondary.main">{versionDetail.summary.discountCount}</Typography>
                      <Typography variant="caption">Discounts</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="h5" color="info.main">{versionDetail.summary.testRuleCount}</Typography>
                      <Typography variant="caption">Test Rules</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`${Math.round((versionDetail.content?.length || 0) / 1024)} KB`} size="small" />
                <Chip label={versionDetail.summary.hasTestRuleSet ? 'Has TestRuleSet' : 'No TestRuleSet'} size="small" variant="outlined" />
                <Chip label={versionDetail.description} size="small" variant="outlined" />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Dialog (Full-screen) */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#1e1e1e', color: 'white', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon />
            <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              FinalAllTravomint.xml
            </Typography>
            <Chip label={`${Math.round(previewXml.length / 1024)} KB`} size="small" sx={{ color: '#aaa', bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Chip label={`${(previewXml.match(/<FeeRule /g) || []).length} rules`} size="small" sx={{ color: '#4caf50', bgcolor: 'rgba(76,175,80,0.15)' }} />
          </Box>
          <Button variant="outlined" size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#0d1117', overflow: 'auto' }}>
          <pre style={{
            margin: 0, padding: 16, fontFamily: '"Consolas", "Fira Code", monospace',
            fontSize: '0.78rem', lineHeight: 1.7, color: '#c9d1d9', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {previewXml}
          </pre>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

